"use client";

import React, { ReactNode, createContext, useContext, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { useMessageAction } from "@/lib/hooks/api/messages";
import { MessagesContextType, MessagesState } from "@/lib/core/crm/types";
import { createInitialState } from "./state";
import { useGetters } from "./getters";
import { useSetters } from "./setters";
import { useActions } from "./actions";
import { useUtils } from "./utils";

/**
 * The context value is ~21 members: one volatile `state` object plus 20
 * functions. Keeping them in a single context forced every consumer to
 * re-render whenever `state` changed, even the ones that only ever call a
 * setter. The value is therefore split in two:
 *
 *  - `MessagesStateContext` — the volatile `MessagesState` snapshot.
 *  - `MessagesApiContext`   — the 20 getters/setters/actions/utils.
 *
 * `useMessagesContext()` is kept as a composition of the two so existing
 * consumers are unchanged.
 */
export type MessagesApi = Omit<MessagesContextType, "state">;

const MessagesStateContext = createContext<MessagesState | undefined>(undefined);
const MessagesApiContext = createContext<MessagesApi | undefined>(undefined);

export function MessagesProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const messageActionMutation = useMessageAction();

  // Initialize state
  const [state, setState] = React.useState(createInitialState());

  // Call hooks at top level (not inside useMemo). Each slice returns a
  // referentially-stable object, so `api` below only changes when a member does.
  const getters = useGetters(state);
  const setters = useSetters(setState);
  const actions = useActions(state, setState, setters.updateMessage, messageActionMutation);
  const utils = useUtils(state, queryClient);

  const api = useMemo<MessagesApi>(
    () => ({
      ...getters,
      ...setters,
      ...actions,
      ...utils,
    }),
    [getters, setters, actions, utils]
  );

  return (
    <MessagesStateContext.Provider value={state}>
      <MessagesApiContext.Provider value={api}>{children}</MessagesApiContext.Provider>
    </MessagesStateContext.Provider>
  );
}

/** Subscribe to the raw messages state only. */
export function useMessagesState(): MessagesState {
  const state = useContext(MessagesStateContext);
  if (state === undefined) {
    throw new Error("useMessagesState must be used within a MessagesProvider");
  }
  return state;
}

/** Subscribe to the getters/setters/actions/utils only. */
export function useMessagesApi(): MessagesApi {
  const api = useContext(MessagesApiContext);
  if (api === undefined) {
    throw new Error("useMessagesApi must be used within a MessagesProvider");
  }
  return api;
}

// Custom hook to use the messages context (state + api composed)
export function useMessagesContext(): MessagesContextType {
  const state = useContext(MessagesStateContext);
  const api = useContext(MessagesApiContext);
  const value = useMemo<MessagesContextType | undefined>(
    () => (state === undefined || api === undefined ? undefined : { state, ...api }),
    [state, api]
  );
  if (value === undefined) {
    throw new Error("useMessagesContext must be used within a MessagesProvider");
  }
  return value;
}
